import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import storageManager from '@/api/storageManager';
import { fireFacebookEvent, initFacebookPixel } from './facebookPixel';
import {
	fireTrafficJunkieEvent,
	initTrafficJunkyIdSync,
} from './trafficJunkiePixel';
import { MARKETING_EVENT_TYPE } from './tracking.constants';

const appendPendingEvent = (queue, event) => [
	...queue,
	{ ...event, queuedAt: Date.now() },
];

const flushPendingEvent = (event) => {
	if (event.type === MARKETING_EVENT_TYPE.FACEBOOK) {
		fireFacebookEvent(event.eventName, event.eventData ?? {});
		return;
	}

	if (event.type === MARKETING_EVENT_TYPE.TRAFFIC_JUNKY) {
		fireTrafficJunkieEvent(event.payload ?? {});
	}
};

const startMarketingPixels = () => {
	initFacebookPixel();
	initTrafficJunkyIdSync();
};

const useMarketingTracking = () => {
	const [isMarketingAllowed, setIsMarketingAllowed] = useState(() =>
		storageManager.cookieConsent.allowsMarketing(),
	);
	const [pendingEvents, setPendingEvents] = useState([]);

	const trackFacebookEvent = useCallback(
		(payload) => {
			const { eventName, eventData = {} } = payload;

			if (!isMarketingAllowed) {
				setPendingEvents((queue) =>
					appendPendingEvent(queue, {
						type: MARKETING_EVENT_TYPE.FACEBOOK,
						eventName,
						eventData,
					}),
				);
				return;
			}

			fireFacebookEvent(eventName, eventData);
		},
		[isMarketingAllowed],
	);

	const trackTrafficJunkieEvent = useCallback(
		(payload) => {
			if (!isMarketingAllowed) {
				setPendingEvents((queue) =>
					appendPendingEvent(queue, {
						type: MARKETING_EVENT_TYPE.TRAFFIC_JUNKY,
						payload,
					}),
				);
				return;
			}

			fireTrafficJunkieEvent(payload);
		},
		[isMarketingAllowed],
	);

	const setMarketingAllowed = (allowed) => {
		const nextAllowed = Boolean(allowed);
		setIsMarketingAllowed(nextAllowed);

		if (!nextAllowed) {
			setPendingEvents([]);
			return;
		}

		startMarketingPixels();
	};

	useLayoutEffect(() => {
		if (!storageManager.cookieConsent.allowsMarketing()) {
			return;
		}

		startMarketingPixels();
	}, []);

	useLayoutEffect(() => {
		if (!isMarketingAllowed || pendingEvents.length === 0) {
			return;
		}

		pendingEvents.forEach(flushPendingEvent);
		setPendingEvents([]);
	}, [isMarketingAllowed, pendingEvents]);

	return useMemo(
		() => ({
			isMarketingAllowed,
			setMarketingAllowed,
			trackFacebookEvent,
			trackTrafficJunkieEvent,
		}),
		[isMarketingAllowed, trackFacebookEvent, trackTrafficJunkieEvent],
	);
};

export default useMarketingTracking;
